// React & Libraries
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Utilities & Helpers
import getImageUrl from "../../../utils/getImageUrl";
import { getLocalizedTranslation } from "../../../utils/localization";

// Redux Actions - Products
import { getSingleProductFunc } from "../../../store/custom-actions/products-actions/GetSingleProductSlicer";
import { updateProductFunc } from "../../../store/custom-actions/products-actions/UpdateProductSlicer";

// Redux Actions - Attributes & Categories
import { getAttributesFunc } from "../../../store/custom-actions/attributes-actions/GetAttributesSlicer";
import { getCategoriesFunc } from "../../../store/custom-actions/categories-actions/GetCategoriesSlicer";

// Custom Feature Components
import AttributeManager from './AttributeManager';
import VariantAttributeCard from './VariantAttributeCard';
import ProductVariantsTable from './ProductVariantsTable';
import MediaSelectorButton from "../../components/media/MediaSelectorButton";

// UI Components
import PageTitle from "../../layouts/PageTitle";
import FormField from "../../components/ui/FormField";
import TextAreaField from "../../components/ui/TextAreaField";
import CheckboxField from "../../components/ui/CheckboxField";
import SelectBox from "../../components/ui/SelectBox";
import SubmitButton from "../../components/ui/SubmitButton";
import NestedTreeSelector from "../../components/ui/NestedTreeSelector";
import LanguageSwitcherTabs from "../../components/ui/LanguageSwitcherTabs";

// Hooks - Product Attributes
import {
  useProductVariants,
  handleToggleAttribute,
  handleToggleAttributeValue,
  rebuildVariantsFromAttributes,
  normalizeVariantsAndAttributes,
} from "../../../hooks/useProductAttributes";


function UpdateProductForm() {
  // React & Redux Hooks
  const { t } = useTranslation();
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Form Setup (React Hook Form)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
    control,
    getValues,
  } = useForm();

  // Redux Selectors
  const { loading: submitLoading } = useSelector((state) => state.updateProductSlicer);
  const { loading: categoriesLoading, data: categoriesList } = useSelector((state) => state.getCategoriesSlicer);
  const { loading: switchLangLoading, data: languageData } = useSelector((state) => state.getLanguageSettingsSlicer);
  const { data: attributesData, loading: attributesLoading } = useSelector((state) => state.getAttributesSlicer);
  const { data: productData, loading } = useSelector((state) => state.getSingleProductSlicer);

  // Local State
  const [categoryId, setCategoryId] = useState("");
  const skuValue = watch("sku");

  const [activeLang, setActiveLang] = useState();
  const [skuCode, setSkuCode] = useState("");
  const [productAttributes, setProductAttributes] = useState({});
  const [productImages, setProductImages] = useState([]);
  const [imagesListViewr, setImagesListViewr] = useState([]);
  const [thumbnailDefault, setThumbnailDefault] = useState([]);
  const [imagesDefault, setImagesDefault] = useState([]);
  const [typesObject, setTypesObject] = useState({});
  const [keyInput, setKeyInput] = useState("");
  const [valueInput, setValueInput] = useState("");
  const [syncEnabled, setSyncEnabled] = useState({
    price: false,
    discount: false,
    discountType: false,
    stock: false
  });
  const { variantFields, append, remove, update } = useProductVariants({
    control,
    skuCode,
    setValue,
  });
  /**
   * useEffect Hooks
   */
  // Set Default Language
  useEffect(() => {
    if (languageData) {
      setActiveLang(languageData?.defaultLang?.slug);
    }
  }, [languageData]);

  // Fetch Initial Data
  useEffect(() => {
    dispatch(getSingleProductFunc(productId));
    dispatch(getCategoriesFunc());
    dispatch(getAttributesFunc());
  }, [dispatch, productId]);

  useEffect(() => {
    if (!productData?.product) return;

    const product = productData.product;
    const imagesList = productData.images || [];
    const translations = getLocalizedTranslation(product.translations, languageData?.defaultLang?.slug);

    reset(buildFormDefaults(product, translations));
    setTypesObject(translations.product_attributes || {});
    setThumbnailDefault(getSelectedThumbnail(product));
    setImagesDefault(getSelectedImages(product, imagesList));
    setProductImages(product.images || []);
    setImagesListViewr(
      getSelectedImages(product, imagesList).map((img) => ({
        value: img.id,
        src: getImageUrl(img.path),
      }))
    );

    const { attributesMap, variants } = normalizeVariantsAndAttributes(product);

    setProductAttributes(attributesMap);
    remove();
    variants.forEach(variant => append(variant));
  }, [productData, languageData, reset]);

  // Language Switching
  useEffect(() => {
    if (!productData?.product?.translations || !activeLang) return;

    const translation = getLocalizedTranslation(productData.product.translations, activeLang);
    setValue("title", translation?.title || "");
    setValue("description", translation?.description || "");
    setValue("product_attributes", translation?.product_attributes || "");
    setTypesObject(translation.product_attributes || {});
    setKeyInput("");
    setValueInput("");
  }, [activeLang, productData, setValue]);


  // Update SKU Code from Watched SKU
  useEffect(() => {
    setSkuCode(skuValue || "");
  }, [skuValue]);

  useEffect(() => {
    changeCategoryId(productData?.product?.category_id);
  }, [productData]);

  /**
   * Handlers
   */
  // Category Change
  const changeCategoryId = (id) => {
    setCategoryId(id);
  };

  // Form Submit
  const onSubmit = async (data) => {
    const cleanedVariants = data.variants?.map((variant) => {
      const { attribute_values_display, ...rest } = variant;

      const attribute_values = attribute_values_display?.map((val) => val.id) || [];

      const cleaned = {
        ...rest,
        attribute_values,
      };

      if (typeof variant.id == "number" && Number.isInteger(variant.id)) {
        cleaned.id = variant.id;
      }

      return cleaned;
    }) || [];

    const payload = {
      ...data,
      lang: activeLang,
      category_id: categoryId,
      product_attributes: typesObject,
      variants: cleanedVariants,
    };

    const result = await dispatch(updateProductFunc({ id: productId, data: payload }));

    if (updateProductFunc.fulfilled.match(result)) {
      reset();
      navigate('/products');
    }
  };



  // Toggle Attribute
  const onAttributeToggle = (attributeId, isChecked) => {
    setProductAttributes((prev) =>
      handleToggleAttribute(attributeId, isChecked, attributesData, prev, (attrs) =>
        rebuildVariantsFromAttributes({
          newAttributes: attrs,
          watch,
          variantFields,
          append,
          remove,
          keepExisting: true,
        })
      )
    );
  };
  // Toggle Attribute Value
  const onAttributeValueToggle = (attributeId, valueId, isChecked) => {
    setProductAttributes((prev) =>
      handleToggleAttributeValue(attributeId, valueId, isChecked, attributesData, prev, (attrs) =>
        rebuildVariantsFromAttributes({
          newAttributes: attrs,
          watch,
          variantFields,
          append,
          remove,
          keepExisting: true,
        })
      )
    );
  };

  // Sync functions
  const syncToVariants = (field, value) => {
    if (!syncEnabled[field] || variantFields.length == 0) return;

    variantFields.forEach((_, index) => {
      update(index, {
        ...getValues(`variants.${index}`),
        [field]: value,
      });
    });
  };

  const handleSyncToggle = (field) => {
    setSyncEnabled(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleFieldChange = (field, value) => {
    setValue(field, value);
    syncToVariants(field, value);
  };

  /**
   * Utility Functions
   */
  function buildFormDefaults(product, translations) {
    return {
      title: translations.title,
      description: translations.description,
      sku: product.sku,
      slug: product.slug,
      price: product.price,
      discount: product.discount,
      discount_type: product.discount_type,
      stock: product.stock,
      min_stock: product.min_stock,
      category_id: product.category_id,
      active: product.active,
      recomended: product.recomended,
      best_selling: product.best_selling,
      track_stock: product.track_stock,
      meta_keywords: product.meta_keywords,
      meta_description: product.meta_description,
      product_attributes: translations.product_attributes,
      images: product.images,
      thumbnail_img: product.thumbnail_img,
    };
  }

  function getSelectedImages(product, imagesList) {
    const imageIdSet = new Set((product.images || []).map(id => id.toString()));
    return imagesList.filter(img => imageIdSet.has(img.id.toString()));
  }

  function getSelectedThumbnail(product) {
    return product.thumbnail ? [product.thumbnail] : [];
  }
  return (
    <>
      <PageTitle motherMenu={"Products"} activeMenu={"Update Item"} motherMenuPath="/products" />
      <LanguageSwitcherTabs
        languages={languageData?.languages || []}
        activeLang={activeLang}
        onChange={setActiveLang}
      />
      <>
        <form id="submit-form" onSubmit={handleSubmit(onSubmit)} >
          <div className="row">
            {/*  Back & submit button */}
            <div className="text-end toolbar toolbar-bottom ">
              <div className="toolbar toolbar-bottom pb-3 d-flex justify-content-end">
                {/* Back Button */}
                <Link to="/products">
                  <button type="button" className="btn btn-secondary sw-btn-back me-2">
                    Back
                  </button>
                </Link>
                <SubmitButton
                  isLoading={submitLoading}
                  form="submit-form"
                />
              </div>
            </div>
            <div className="col-xl-12 col-xxl-12">
              <div className="row">
                <div className="col-12 col-lg-8">
                  <div className="card">
                    <h5 className="card-title p-4"> {t('Product details')}</h5>
                    <div className="card-body">
                      <>
                        <div>
                          <FormField
                            label={`${t("Title")} (${activeLang})`}
                            name="title"
                            register={register}
                            required
                            errors={errors}
                          />
                          <div className="form-group mb-3">
                            <label>{t("Category")}</label>
                            <NestedTreeSelector
                              treeData={categoriesList}
                              onSelect={changeCategoryId}
                              loading={categoriesLoading}
                              defaultValue={productData?.product?.category_id}
                            />
                          </div>
                          <FormField
                            label="SKU Code"
                            name="sku"
                            register={register}
                            required
                            errors={errors}
                          />
                          <FormField
                            label="Slug"
                            name="slug"
                            register={register}
                            required
                            errors={errors}
                          />
                          {/* Stock Field with Sync */}
                          <div className="form-group mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label mb-0">
                                {t("Product Stock")} <span className="text-danger">*</span>
                              </label>
                              {variantFields.length > 0 && (
                                <div className="sync-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sync-stock"
                                    checked={syncEnabled.stock}
                                    onChange={() => handleSyncToggle('stock')}
                                  />
                                  <label className="form-check-label" htmlFor="sync-stock">
                                    <i className="fas fa-sync me-1"></i>
                                    {t("Sync to Variants")}
                                  </label>
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              className="form-control"
                              min="0"
                              {...register("stock", {
                                required: t("Field is required"),
                                onChange: (e) => handleFieldChange('stock', e.target.value)
                              })}
                            />
                            {errors.stock && (
                              <p className="text-red-600 text-sm">{t(errors.stock.message)}</p>
                            )}
                          </div>

                          <FormField
                            label="Min Stock"
                            name="min_stock"
                            register={register}
                            required
                            errors={errors}
                          />
                          <TextAreaField
                            label="Description"
                            name="description"
                            isRichTextEditor
                            register={register}
                            required
                            errors={errors}
                            watch={watch}
                            setValue={setValue}
                          />

                        </div>
                      </>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-4">
                  <div className="row ">
                    {/* product status  */}
                    <div className="col-12">
                      <div className="card">
                        <h5 className="card-title  p-4">{t('Product status')}</h5>
                        <div className="card-body">
                          <>
                            <CheckboxField
                              name="active"
                              label="Activity"
                              register={register}
                              errors={errors}
                            />
                            <CheckboxField
                              name="recomended"
                              label="Recomended"
                              register={register}
                              errors={errors}
                            />
                            {/* <CheckboxField
                              name="best_selling"
                              label="Best Selling"
                              register={register}
                              errors={errors}
                            /> */}
                            <CheckboxField
                              name="track_stock"
                              label="Track Stock"
                              register={register}
                              errors={errors}
                            />
                          </>
                        </div>
                      </div>
                    </div>
                    {/* product price  */}
                    <div className="col-12">
                      <div className="card">
                        <h5 className="card-title  p-4">
                          {t('Product pricing')}
                          {variantFields.length > 0 && (
                            <span className="sync-available-badge">
                              <i className="fas fa-sync me-1"></i>
                              {t('Sync Available')}
                            </span>
                          )}
                        </h5>
                        <div className="card-body">
                          <>
                            {/* Price Field with Sync */}
                            <div className="form-group mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label mb-0">
                                  {t("Price")} <span className="text-danger">*</span>
                                </label>
                                {variantFields.length > 0 && (
                                  <div className="sync-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="sync-price"
                                      checked={syncEnabled.price}
                                      onChange={() => handleSyncToggle('price')}
                                    />
                                    <label className="form-check-label" htmlFor="sync-price">
                                      <i className="fas fa-sync me-1"></i>
                                      {t("Sync to Variants")}
                                    </label>
                                  </div>
                                )}
                              </div>
                              <input
                                type="number"
                                className="form-control"
                                step="0.01"
                                {...register("price", {
                                  required: t("Field is required"),
                                  onChange: (e) => handleFieldChange('price', e.target.value)
                                })}
                              />
                              {errors.price && (
                                <p className="text-red-600 text-sm">{t(errors.price.message)}</p>
                              )}
                            </div>

                            {/* Discount Field with Sync */}
                            <div className="form-group mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label mb-0">
                                  {t("Discount")} <span className="text-danger">*</span>
                                </label>
                                {variantFields.length > 0 && (
                                  <div className="sync-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="sync-discount"
                                      checked={syncEnabled.discount}
                                      onChange={() => handleSyncToggle('discount')}
                                    />
                                    <label className="form-check-label" htmlFor="sync-discount">
                                      <i className="fas fa-sync me-1"></i>
                                      {t("Sync to Variants")}
                                    </label>
                                  </div>
                                )}
                              </div>
                              <input
                                type="number"
                                className="form-control"
                                step="0.01"
                                min="0"
                                {...register("discount", {
                                  required: t("Field is required"),
                                  onChange: (e) => handleFieldChange('discount', e.target.value)
                                })}
                              />
                              {errors.discount && (
                                <p className="text-red-600 text-sm">{t(errors.discount.message)}</p>
                              )}
                            </div>

                            {/* Discount Type Field with Sync */}
                            <div className="form-group mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label mb-0">
                                  {t("Discount Type")} <span className="text-danger">*</span>
                                </label>
                                {variantFields.length > 0 && (
                                  <div className="sync-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="sync-discount-type"
                                      checked={syncEnabled.discountType}
                                      onChange={() => handleSyncToggle('discountType')}
                                    />
                                    <label className="form-check-label" htmlFor="sync-discount-type">
                                      <i className="fas fa-sync me-1"></i>
                                      {t("Sync to Variants")}
                                    </label>
                                  </div>
                                )}
                              </div>
                              <select
                                className="form-select"
                                {...register("discount_type", {
                                  required: t("Field is required"),
                                  onChange: (e) => handleFieldChange('discount_type', e.target.value)
                                })}
                              >
                                <option value="fixed">{t("Fixed")}</option>
                                <option value="percentage">{t("Percentage")}</option>
                              </select>
                              {errors.discount_type && (
                                <p className="text-red-600 text-sm">{t(errors.discount_type.message)}</p>
                              )}
                            </div>
                          </>
                        </div>
                      </div>
                    </div>
                    {/* product media  */}
                    <div className="col-12">
                      <div className="card">
                        <h5 className="card-title p-4">{t("Product Media")}</h5>
                        <div className="card-body">
                          <MediaSelectorButton
                            inputName="thumbnail_img"
                            required
                            register={register}
                            errors={errors}
                            onChange={(value) => setValue("thumbnail_img", value)}
                            defaultSelected={thumbnailDefault}
                          />
                          <MediaSelectorButton
                            label="Images"
                            inputName="images"
                            selectionType="multiple"
                            required
                            register={register}
                            errors={errors}
                            onChange={(ids, images) => {
                              setValue("images", ids);
                              setProductImages(ids);
                              setImagesListViewr(images.map(img => ({
                                value: img.id,
                                src: getImageUrl(img.path),
                              })));
                            }}
                            defaultSelected={imagesDefault}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* product meta  */}
              <div className="col-12">
                <div className="card">
                  <h5 className="card-title  p-4">{t('Meta product')}</h5>
                  <div className="card-body">
                    <>
                      <TextAreaField
                        label="Meta description"
                        name="meta_description"
                        register={register}
                        errors={errors}
                        placeholder="Enter meta description"
                      />
                      <FormField
                        label="Meta keyword"
                        name="meta_keywords"
                        register={register}
                        errors={errors}
                      />
                    </>
                  </div>
                </div>
              </div>
              <div className="col-12">
                {/* product attributes */}
                <div className="card">
                  <h5 className="card-title  p-4">{t('Product attributes')}</h5>
                  <div className="card-body">
                    <>
                      <AttributeManager
                        typesObject={typesObject}
                        setTypesObject={setTypesObject}
                        keyInput={keyInput}
                        valueInput={valueInput}
                        setKeyInput={setKeyInput}
                        setValueInput={setValueInput}
                      />
                    </>
                  </div>
                </div>
              </div>
              {/* Product Variant */}
              {!attributesLoading &&
                attributesData?.map((attribute, index) => (
                  <div key={`${attribute.id}-${index}`} className="col-12">
                    <VariantAttributeCard
                      attribute={attribute}
                      selected={!!productAttributes[attribute.id]}
                      selectedValues={productAttributes[attribute.id]?.values || []}
                      onToggleAttribute={onAttributeToggle}
                      onToggleValue={onAttributeValueToggle}
                    />
                  </div>
                ))}
              <div className="col-12">
                {Object.keys(productAttributes).length >= 1 && (
                  <ProductVariantsTable
                    productAttributes={productAttributes}
                    variantFields={variantFields}
                    productImages={productImages}
                    imagesListViewr={imagesListViewr}
                    register={register}
                    getValues={getValues}
                    update={update}
                    watch={watch}
                    skuCode={skuCode}
                    syncEnabled={syncEnabled}
                    onSyncFieldChange={handleFieldChange}
                  />
                )}
              </div>

            </div>
          </div>
        </form>
      </>
    </>
  );
}

export default UpdateProductForm;
